import React, { createContext, useContext, useEffect, useState } from 'react';
import authStore from '../../store/auth';
import { reportError } from '../../services/logger';

const SignalRContext = createContext({});

/* withAutomaticReconnect runs through its intervals in about a minute and then gives up for good.
   Before these, that was the end of live updates for the rest of the session: onclose fired, every
   listener unsubscribed, and nothing ever started the hub again — the only cure was a page reload.
   A tab left open over lunch came back deaf, and the tables it feeds (extraction, transformation,
   report status) sat on whatever they had loaded. */
const RETRY_DELAYS = [5000, 10000, 30000, 60000];

export const SignalRProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState(null);
  const [signalRService, setSignalRService] = useState(null);

  useEffect(() => {
    if (!authStore.token) return undefined;

    let cancelled = false;
    let hub = null;
    let retryTimer = null;
    let retryAttempt = 0;
    /* Lifecycle handlers belong to one connection object. initialize() hands back the existing one
       while it is still usable, so without this the same handlers stack up on every attempt. */
    let handlersAttachedTo = null;
    /* One line per outage, not one per attempt. Retrying forever is the point of this effect, and a
       stack trace every sixty seconds would bury whatever else the console is trying to say — local
       dev has no negotiate endpoint at all, so there every attempt fails by design. */
    let hasLoggedFailure = false;

    const clearRetry = () => {
      if (retryTimer == null) return;
      window.clearTimeout(retryTimer);
      retryTimer = null;
    };

    const scheduleRetry = () => {
      if (cancelled || retryTimer != null) return;
      const delay = RETRY_DELAYS[Math.min(retryAttempt, RETRY_DELAYS.length - 1)];
      retryAttempt += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const attachHandlers = connection => {
      if (!connection || handlersAttachedTo === connection) return;
      if (!hub) return;
      handlersAttachedTo = connection;

      hub.onReconnecting(() => {
        if (!cancelled) setIsConnected(false);
      });
      hub.onReconnected(() => {
        if (cancelled) return;
        retryAttempt = 0;
        setIsConnected(true);
        setConnectionId(hub.getConnectionState().connectionId);
      });
      hub.onClose(() => {
        if (cancelled) return;
        setIsConnected(false);
        setConnectionId(null);
        scheduleRetry();
      });
    };

    const connect = async () => {
      if (cancelled || !authStore.token) return;

      try {
        if (!hub) {
          hub = (await import('../../services/signalr.service')).default;
          if (cancelled) return;
          setSignalRService(hub);
        }
        // localAccountId is the OID claim — the same value the backend uses as azure_object_id
        const userId = authStore.user?.localAccountId ?? null;
        const connection = await hub.initialize(userId);
        if (cancelled) return;
        attachHandlers(connection);

        await hub.start();
        if (cancelled) return;

        const state = hub.getConnectionState();
        retryAttempt = 0;
        hasLoggedFailure = false;
        setIsConnected(state.isConnected);
        setConnectionId(state.connectionId);
      } catch (error) {
        // A stop from cleanup aborts start() on purpose; that is not a failure to report.
        if (cancelled) return;
        // A slow negotiate is retried below; only a timeout that outlasts a few tries is reported.
        const transientTimeout = error?.code === 'ECONNABORTED' && retryAttempt < 2;
        if (!hasLoggedFailure && !transientTimeout) {
          hasLoggedFailure = true;
          reportError('signalr.init', error);
        }
        setIsConnected(false);
        setConnectionId(null);
        scheduleRetry();
      }
    };

    /* A laptop that was asleep, a tunnel, a VPN that dropped: once the tab is looked at again or the
       network answers, there is no reason to sit out the rest of a backoff. */
    const reconnectNow = () => {
      if (cancelled || document.hidden) return;
      if (hub?.getConnectionState().isConnected) return;
      clearRetry();
      retryAttempt = 0;
      connect();
    };

    /* A frozen tab loses its socket, and a hidden one is frozen sooner or later — unless it holds a
       Web Lock, which Chrome and Edge both leave running. Shared, so every open tab can hold it. */
    const lockAbort = new AbortController();
    let releaseLock = null;
    navigator.locks
      ?.request('datox-signalr', { mode: 'shared', signal: lockAbort.signal }, () => {
        if (cancelled) return undefined;
        return new Promise(resolve => {
          releaseLock = resolve;
        });
      })
      .catch(() => {
        /* Aborted on unmount before it was granted. */
      });

    connect();
    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', reconnectNow);

    return () => {
      cancelled = true;
      lockAbort.abort();
      releaseLock?.();
      clearRetry();
      window.removeEventListener('online', reconnectNow);
      document.removeEventListener('visibilitychange', reconnectNow);
      hub?.stop();
    };
  }, [authStore.token]);

  const contextValue = {
    isConnected,
    connectionId,
    service: signalRService
  };

  return <SignalRContext.Provider value={contextValue}>{children}</SignalRContext.Provider>;
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }

  return context;
};

export default SignalRProvider;
